/**
 * Pure helpers for the Common Client Notification template
 * (pages/admin/CommonClientNotificationPanel.jsx). No React, no network — tested in
 * clientAnnouncement.test.js. The backend re-validates everything
 * (backend/models/client_announcement.py).
 */

// Same rule as components/EmailTemplateRecipients EMAIL_RE and the backend (no
// whitespace anywhere, so no header injection). Not imported from there: that
// module imports @/App, which this pure module must not load.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let seq = 0;
export const uid = (prefix = "ca") => `${prefix}-${++seq}`;

/**
 * Typed/pasted addresses → { added, invalid, duplicates, taken }.
 * Accepts "a@x.com" and (when allowNames) "Name <a@x.com>", separated by commas,
 * semicolons or new lines. `existing` = addresses already in THIS list;
 * `elsewhere` = { email(lowercase): "CC" | … } for addresses in the other lists.
 */
export function parseAddresses(text, { existing = [], elsewhere = {}, allowNames = false } = {}) {
  const seen = new Set(existing.map((e) => e.toLowerCase()));
  const added = [];
  const invalid = [];
  const taken = [];
  let duplicates = 0;
  (text || "").split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean).forEach((token) => {
    const named = allowNames ? token.match(/^(.*?)\s*<([^<>]+)>$/) : null;
    const email = (named ? named[2] : token).trim();
    const name = named ? named[1].replace(/^["']|["']$/g, "").trim() : "";
    const key = email.toLowerCase();
    if (!EMAIL_RE.test(email)) invalid.push(token);
    else if (seen.has(key)) duplicates += 1;
    else if (elsewhere[key]) taken.push(`${email} (already in ${elsewhere[key]})`);
    else {
      seen.add(key);
      added.push({ _key: uid("rcpt"), email, name });
    }
  });
  return { added, invalid, duplicates, taken };
}

/** Why Trigger Now cannot send (null = it can). Mirrors the backend rules.
 *  `locationErrors` = problems in the uploaded locations file (0 when none uploaded). */
export function sendBlocker({ to, cc, bcc, message, limits, locationErrors = 0 }) {
  if (!to.length) return "Add at least one To recipient.";
  const lists = { To: to.map((r) => r.email), CC: cc.map((r) => r.email), BCC: bcc.map((r) => r.email) };
  const owner = {};
  for (const [label, emails] of Object.entries(lists)) {
    const bad = emails.filter((e) => !EMAIL_RE.test(e));
    if (bad.length) return `Invalid ${label} address(es): ${bad.join(", ")}`;
    for (const e of emails) {
      if (owner[e.toLowerCase()]) return `${e} is in both ${owner[e.toLowerCase()]} and ${label}.`;
      owner[e.toLowerCase()] = label;
    }
  }
  if (limits && to.length > limits.max_recipients) return `At most ${limits.max_recipients} To recipients per send.`;
  if (limits && (cc.length > limits.max_copy_recipients || bcc.length > limits.max_copy_recipients)) {
    return `At most ${limits.max_copy_recipients} CC and ${limits.max_copy_recipients} BCC addresses.`;
  }
  if (!message.subject.trim()) return "Enter a subject.";
  if (!message.intro.trim()) return "Enter a message.";
  if (locationErrors) {
    return `The locations file has ${locationErrors} problem(s) — fix the file and upload it again, or remove it.`;
  }
  return null;
}

/** The To/CC/BCC part of a send. */
export const recipientsPayload = ({ to, cc, bcc }) => ({
  recipients: to.map((r) => ({ email: r.email, name: r.name?.trim() || null })),
  cc_emails: cc.map((r) => r.email),
  bcc_emails: bcc.map((r) => r.email),
});

/**
 * The page's lists → the saved notification_configs fields (POST / PUT
 * …/notification-config/admin). Greeting names go in recipient_names, keyed by the
 * lower-cased To address; the whole map is sent on every save, so a removed To
 * takes its name with it.
 */
export const configPayload = ({ to, cc, bcc }) => ({
  recipient_emails: to.map((r) => r.email),
  cc_emails: cc.map((r) => r.email),
  bcc_emails: bcc.map((r) => r.email),
  recipient_names: Object.fromEntries(to.filter((r) => r.name?.trim()).map((r) => [r.email.toLowerCase(), r.name.trim()])),
});

/** A saved notification_configs doc (or configPayload output) → the page's editable { to, cc, bcc } lists. */
export const listsFromSaved = (saved) => {
  const names = saved?.recipient_names || {};
  return {
    to: (saved?.recipient_emails || []).map((email) => ({
      _key: uid("rcpt"), email, name: names[email.toLowerCase()] || "",
    })),
    cc: (saved?.cc_emails || []).map((email) => ({ _key: uid("rcpt"), email, name: "" })),
    bcc: (saved?.bcc_emails || []).map((email) => ({ _key: uid("rcpt"), email, name: "" })),
  };
};

/** True when the page's lists differ from the saved config (order counts). */
export const recipientsChanged = (lists, saved) =>
  JSON.stringify(configPayload(lists)) !== JSON.stringify(configPayload(listsFromSaved(saved)));

/** POST …/send body. Only the SELECTED type's draft is passed in. */
export const sendPayload = ({ type, to, cc, bcc, message, locations = [], idempotencyKey }) => ({
  announcement_type: type,
  locations,
  subject: message.subject,
  intro: message.intro,
  closing: message.closing,
  ...recipientsPayload({ to, cc, bcc }),
  idempotency_key: idempotencyKey,
});

/** The send's idempotency key — one per confirmed send. */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `ca-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
